import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import head from 'lodash/head';
import map from 'lodash/map';
import reduce from 'lodash/reduce';
import remove from 'lodash/remove';

import Box from '@mui/material/Box';

import WebAPIClient from '../../../../../api';
import { DEMAND_APP_ID } from '../../../../../store/demand';
import { current, getLocalTimzone } from '../../../../../helpers/date';
import ModelingChartToolbar from './Toolbar';
import ModelingChart from './Chart';

const compileModelingData = (rawData, range) => {
  let _data = [];
  let time = range.start.unix();
  const initialData = cloneDeep(rawData);

  while (time <= range.end.unix()) {
    const values = reduce(
      initialData, // eslint-disable-next-line
      (acc, metric) => {
        let dataPoint = head(remove(metric.data, { timestamp: time }));
        if (dataPoint) {
          acc[metric.metric] = dataPoint.value;
        }
        return acc;
      },
      {}
    );
    _data.push({ timestamp: time, ...values });
    time += 300;
  }
  return _data;
};

function ModelingChartContainer() {
  const rtos = useSelector((state) => state.demand.rtos);

  const [rto, setRTO] = useState({});
  const [loading, setLoading] = useState(false);
  const [rawData, setRawData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [selectedMetrics, setSelectedMetrics] = useState([
    'inst_loads',
    'frcst_inst_loads',
  ]);

  const [timezone, setTimezone] = useState(getLocalTimzone());
  const [range, setRange] = useState({
    key: 0,
    label: 'Current',
    ...current(getLocalTimzone().zone),
  });

  // track in-flight fetch so range/metric changes abort and restart
  const fetchIdRef = useRef(0);

  useEffect(() => {
    setRTO(find(rtos, { rto_id: 'PJM' }));
  }, [rtos]);

  useEffect(() => {
    if (!loading && rawData.length > 0) {
      setChartData(compileModelingData(rawData, range));
    }
    /* eslint-disable-next-line */
  }, [rawData]);

  // Auto-fetch whenever range, metrics, or rto changes
  useEffect(() => {
    if (!rto?.rto_id) return;
    fetchChartData();
    /* eslint-disable-next-line */
  }, [range, selectedMetrics, rto]);

  const fetchChartData = (_range = range) => {
    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    Promise.all(
      map(selectedMetrics, async (metric) => ({
        metric,
        data: await new WebAPIClient(DEMAND_APP_ID).GET(
          `/resource/${metric}/${rto.rto_id}`,
          { start: _range.start.unix(), end: _range.end.unix() }
        ),
      }))
    )
      .then((payloads) => {
        if (fetchId !== fetchIdRef.current) return; // stale response
        setRawData(payloads);
      })
      .catch((err) => console.error(err))
      .finally(() => {
        if (fetchId === fetchIdRef.current) setLoading(false);
      });
  };

  return (
    <Box sx={{ width: '100%', mt: 1 }}>
      <ModelingChartToolbar
        selectedMetrics={selectedMetrics}
        setSelectedMetrics={setSelectedMetrics}
        range={range}
        setRange={setRange}
        timezone={timezone}
        setTimezone={setTimezone}
      />
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 8,
          p: 1,
          mt: 1.5,
          width: '100%',
        }}>
        <ModelingChart
          chartData={chartData}
          selectedMetrics={selectedMetrics}
          range={range}
          timezone={timezone}
          loading={loading}
        />
      </Box>
    </Box>
  );
}

export default ModelingChartContainer;